import store from '@/store/BookingStore'
import { BookingFunnelService } from '@/service/BookingFunnelService/BookingFunnelService'
import type { Traveller } from '../../../../requests-module/src'
import {
    hasWidgetPassengerIdentity,
    hasWidgetPassengersPrefill,
    widgetSlotToTravellerFields,
    type WidgetPassengerPrefill,
} from '@/logic/widgetMode/widgetPassengersPrefill'

const hasText = (value: string | null | undefined): boolean =>
    typeof value === 'string' && value.trim().length > 0

/**
 * Build travellers with widget `wpax` / sessionStorage identity merged by index.
 */
export const buildTravellersFromWidgetPrefill = (
    travellers: Traveller[],
    list: WidgetPassengerPrefill[],
): Traveller[] =>
    travellers.map((traveller, index) => {
        const slot = list[index]
        if (!slot) return traveller

        const fields = widgetSlotToTravellerFields(slot)
        return {
            ...traveller,
            firstName: fields.firstName ?? traveller.firstName,
            lastName: fields.lastName ?? traveller.lastName,
            birthday: fields.birthday || traveller.birthday,
            gender: fields.gender ?? traveller.gender,
            fulfilled: fields.fulfilled ?? traveller.fulfilled,
        }
    })

const needsBackendParticipantPersist = (list: WidgetPassengerPrefill[]): boolean =>
    list.some((slot) => {
        if (!hasWidgetPassengerIdentity(slot)) return false
        const fields = widgetSlotToTravellerFields(slot)
        return hasText(fields.firstName) || hasText(fields.lastName)
    })

/**
 * Persist widget host passengers (`wpax` URL param or sessionStorage) to DynamoDB.
 * Must run even when Vuex already shows names — commitBooking merges widget data
 * into the store on every GET, which previously made hasChanges=false and skipped the PUT.
 */
export const prefillParticipantsFromWidget = async (opts?: {
    alternative?: 'flight' | 'transfer' | 'service' | 'train';
}): Promise<void> => {
    if (!store.getters.getIsWidgetMode) return

    const prefill = store.getters.getWidgetPassengersPrefill
    if (!hasWidgetPassengersPrefill(prefill)) return

    const booking = store.getters.getBooking
    if (!booking?.travellers?.length) return

    const list = prefill!.list ?? []
    const updatedTravellers = buildTravellersFromWidgetPrefill(booking.travellers, list)

    if (!needsBackendParticipantPersist(list)) {
        return
    }

    await BookingFunnelService.updateParticipants(
        updatedTravellers,
        undefined,
        opts?.alternative ?? 'service',
    )
}
